var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { getIncomePointEdge, INCOME_POINT_EDGE_KEY, } from "@rentiohq/shared-frontend/dist/utils/rentmatch.utils";
import { Banner } from "../../../Banner";
import { Button } from "../../../Button";
import { Icon } from "../../../Icon";
import { BaseInput } from "../../components/BaseInput";
import * as S from "./IncomeRangesField.styled";
/**
 * The office's income curve: at each rent, the multiple of it a candidate needs
 * for a perfect score.
 *
 * Points, not bands (DEV-3265). Between two rows the multiple runs straight,
 * and outside the outermost rows it stays flat, which is why the first and last
 * rows say so in words. Read as bands this jumped at every boundary: one euro
 * more rent could lower the income demanded by nearly two hundred.
 *
 * One field holding every row, because a row on its own decides nothing and the
 * table is stored and read as one value. How many there are is up to the office.
 */
var MINIMUM_ROWS = 1;
/** A multiple of the rent moves in tenths, so the arrows step by a tenth. */
var RATIO_STEP = 0.1;
var parseRatio = function (typed) {
    var cleaned = "".concat(typed).replace(",", ".");
    if (cleaned === "") {
        return null;
    }
    var asNumber = Number(cleaned);
    return isFinite(asNumber) ? asNumber : null;
};
var formatRatio = function (ratio) { return "".concat(ratio).replace(".", ","); };
var parseAmount = function (typed) {
    var cleaned = "".concat(typed).replace(/[^0-9]/g, "");
    return cleaned === "" ? null : Number(cleaned);
};
export var IncomeRangesField = function (props) {
    var idSchema = props.idSchema, formData = props.formData, _a = props.rawErrors, rawErrors = _a === void 0 ? [] : _a, onChange = props.onChange;
    var rows = Array.isArray(formData) ? formData : [];
    var lastIndex = rows.length - 1;
    var setRow = function (index, next) {
        return onChange(rows.map(function (row, i) { return (i === index ? __assign(__assign({}, row), next) : row); }));
    };
    // A new row starts where the curve currently ends, which is the only place a
    // broker can add one without reordering what is already there.
    var addRow = function () {
        var last = rows[lastIndex];
        onChange(__spreadArray(__spreadArray([], rows, true), [
            {
                maxRent: typeof (last === null || last === void 0 ? void 0 : last.maxRent) === "number" ? last.maxRent + 500 : 500,
                ratio: last === null || last === void 0 ? void 0 : last.ratio,
            },
        ], false));
    };
    var removeRow = function (index) {
        return onChange(rows.filter(function (_, i) { return i !== index; }));
    };
    /**
     * A dearer row asking a higher multiple than a cheaper one turns the idea
     * around: the curve asks proportionally less as the rent rises, because twice
     * a high rent already leaves plenty over.
     *
     * Warned about rather than refused. It is almost always a slip, but an office
     * may have a reason nobody here thought of, and this is their setting.
     */
    var isRising = function (index) {
        var _a, _b;
        var ratio = (_a = rows[index]) === null || _a === void 0 ? void 0 : _a.ratio;
        var above = (_b = rows[index - 1]) === null || _b === void 0 ? void 0 : _b.ratio;
        return (index > 0 &&
            typeof ratio === "number" &&
            typeof above === "number" &&
            ratio > above);
    };
    // The first offending row, named with its own numbers. "A dearer row asks
    // more than a cheaper one" is true and says nothing a broker can act on;
    // their own figures say where to look.
    var risingIndex = rows.findIndex(function (_, index) { return isRising(index); });
    var uniqueErrors = Array.from(new Set(rawErrors));
    /**
     * The one thing here that refuses a save: a rent that does not climb past the
     * row above it. The same predicate the form's validation applies, so the ring
     * and the message can never disagree about which row is at fault.
     */
    var isOutOfOrder = function (index) {
        var _a, _b;
        var rent = (_a = rows[index]) === null || _a === void 0 ? void 0 : _a.maxRent;
        var above = (_b = rows[index - 1]) === null || _b === void 0 ? void 0 : _b.maxRent;
        return (index > 0 &&
            typeof rent === "number" &&
            typeof above === "number" &&
            rent <= above);
    };
    return (_jsxs(_Fragment, { children: [_jsxs(S.Table, { children: [_jsxs(S.HeadRow, { children: [_jsx(S.RentCell, { children: getLocalizedText("broker.rentmatch.income_ranges.column.rent") }), _jsx(S.ScoreCell, { children: getLocalizedText("broker.rentmatch.income_ranges.column.income") })] }), rows.map(function (row, index) {
                        var _a, _b;
                        var edge = getIncomePointEdge(index, rows.length);
                        return (_jsxs(S.Row, { "$isOdd": index % 2 === 1, children: [_jsxs(S.RentCell, { children: [_jsx(S.RentField, { "$hasError": isOutOfOrder(index), children: _jsx(BaseInput, { id: "".concat(idSchema.$id, "-rent-").concat(index), size: "extraSmall", value: (_a = row.maxRent) !== null && _a !== void 0 ? _a : "", suffix: "EUR", onChange: function (typed) {
                                                    return setRow(index, { maxRent: parseAmount(typed) });
                                                } }) }), edge && (_jsx(S.Text, { children: getLocalizedText(INCOME_POINT_EDGE_KEY[edge]) }))] }), _jsxs(S.ScoreCell, { children: [_jsx(S.RatioField, { "$isWarning": isRising(index), children: _jsx(BaseInput, { id: "".concat(idSchema.$id, "-ratio-").concat(index), size: "extraSmall", type: "number", step: RATIO_STEP, min: 1, value: (_b = row.ratio) !== null && _b !== void 0 ? _b : "", onChange: function (typed) {
                                                    return setRow(index, { ratio: parseRatio(typed) });
                                                } }) }), _jsx(S.Strong, { children: getLocalizedText("broker.rentmatch.income_ranges.standard.row.factor.suffix") }), typeof row.maxRent === "number" &&
                                            typeof row.ratio === "number" && (_jsx(S.Amount, { children: getLocalizedText("broker.rentmatch.income_ranges.standard.row.amount", {
                                                amount: formatCurrency(Math.round(row.maxRent * row.ratio), false),
                                            }) }))] }), _jsx(S.RemoveCell, { children: rows.length > MINIMUM_ROWS && (_jsx(Button, { appearance: "plain", onClick: function () { return removeRow(index); }, ariaLabel: getLocalizedText("broker.rentmatch.income_ranges.row.remove"), children: _jsx(Icon, { source: "bin", color: "red", size: "small" }) })) })] }, index));
                    }), _jsx(S.AddRow, { "$isOdd": rows.length % 2 === 1, children: _jsx(Button, { appearance: "link", onClick: addRow, children: _jsxs(S.AddLabel, { children: [_jsx(Icon, { source: "addCircle", size: "small" }), getLocalizedText("broker.rentmatch.income_ranges.add_row")] }) }) })] }), rows.length > 1 && (_jsx(S.Note, { children: _jsxs(S.NoteLabel, { children: [_jsx(Icon, { source: "informationCircle", size: "small" }), getLocalizedText("broker.rentmatch.income_ranges.between_rows")] }) })), risingIndex > 0 && (_jsx(S.Warning, { children: _jsx(Banner, { hasDismiss: false, variation: "warning", title: getLocalizedText("broker.rentmatch.income_ranges.warning.rising", {
                        rent: formatCurrency(rows[risingIndex].maxRent, false),
                        ratio: formatRatio(rows[risingIndex].ratio),
                        rentAbove: formatCurrency(rows[risingIndex - 1].maxRent, false),
                        ratioAbove: formatRatio(rows[risingIndex - 1].ratio),
                    }) }) })), uniqueErrors.length > 0 && (_jsx(S.Warning, { children: _jsx(Banner, { hasDismiss: false, variation: "error", title: uniqueErrors[0], children: uniqueErrors.slice(1).map(function (error) { return (_jsx("div", { children: error }, error)); }) }) }))] }));
};
